const DIAGNOSTICS_KEY = "erp_client_diagnostics";
const DIAGNOSTICS_SCHEMA_VERSION = 2;
const MAX_RECORDS = 40;

const ALLOWED_EVENTS = new Set([
  "scope_not_ready",
  "auth_bootstrap_failed",
  "request_failed",
  "request_superseded",
  "session_expired",
  "empty_canonical_result",
  "offline_waiting",
  "offline_recovered",
]);
const ALLOWED_ROLES = new Set([
  "parent",
  "teacher",
  "management",
  "student",
  "public",
  "unknown",
]);
const ALLOWED_OPERATIONS = new Set([
  "authentication",
  "ui_render",
  "parent_home",
  "parent_homework",
  "parent_messages",
  "parent_fees",
  "parent_results",
  "parent_coming_up",
  "parent_school_week",
  "parent_daily_test",
  "parent_profile",
  "parent_attendance",
  "parent_data_read",
  "parent_data_write",
  "api_read",
  "api_write",
]);
const ALLOWED_FAILURES = new Set([
  "access_denied",
  "not_found",
  "server_unavailable",
  "network_unavailable",
  "timeout",
  "render_failed",
  "unknown_failure",
]);
const ALLOWED_AUTH_OUTCOMES = new Set(["bootstrap_failed", "expired"]);

const PARENT_ROUTES = Object.freeze({
  "/parent/home": ["parent_home", "/parent/home"],
  "/parent/homework": ["parent_homework", "/parent/homework"],
  "/parent/messages": ["parent_messages", "/parent/messages"],
  "/parent/meet-school": ["parent_messages", "/parent/messages"],
  "/parent/fees": ["parent_fees", "/parent/fees"],
  "/parent/results": ["parent_results", "/parent/results"],
  "/parent/calendar": ["parent_coming_up", "/parent/calendar"],
  "/parent/school-week": ["parent_school_week", "/parent/school-week"],
  "/parent/timetable": ["parent_school_week", "/parent/school-week"],
  "/parent/course-plan": ["parent_school_week", "/parent/school-week"],
  "/parent/zero-period": ["parent_daily_test", "/parent/zero-period"],
  "/parent/profile": ["parent_profile", "/parent/profile"],
  "/parent/attendance": ["parent_attendance", "/parent/attendance"],
});
const ALLOWED_ROUTE_NAMES = new Set([
  ...Object.values(PARENT_ROUTES).map(([name]) => name),
  "teacher_portal",
  "management_portal",
  "student_portal",
  "login",
  "public",
]);

const releaseSha = () => {
  const value = String(process.env.REACT_APP_RELEASE_SHA || "").trim();
  return /^[0-9a-f]{40}$/i.test(value) ? value.toLowerCase() : "unknown";
};

const clientVersion = () => {
  const value = String(process.env.REACT_APP_CLIENT_VERSION || "").trim();
  return /^\d+\.\d+\.\d+(?:-[a-z0-9.-]{1,24})?$/i.test(value)
    ? value
    : "unknown";
};

const cleanPathname = (value) =>
  `/${String(value || "")
    .split(/[?#]/, 1)[0]
    .split("/")
    .filter(Boolean)
    .join("/")}`.toLowerCase();

export const resolveSafeRouteName = (pathname = "") => {
  const path = cleanPathname(pathname);
  if (PARENT_ROUTES[path]) return PARENT_ROUTES[path][0];
  if (path.startsWith("/parent")) return "parent_home";
  if (path.startsWith("/teacher")) return "teacher_portal";
  if (path.startsWith("/dashboard") || path.startsWith("/management"))
    return "management_portal";
  if (path.startsWith("/student")) return "student_portal";
  if (path.includes("login")) return "login";
  return "public";
};

export const resolveRoleCategory = (pathname = "") => {
  const path = cleanPathname(pathname);
  if (path.startsWith("/parent")) return "parent";
  if (path.startsWith("/teacher")) return "teacher";
  if (path.startsWith("/student")) return "student";
  if (path.startsWith("/dashboard") || path.startsWith("/management"))
    return "management";
  if (path.includes("login") || path === "/") return "public";
  return "unknown";
};

export const resolveSafeOperation = (path = "", method = "GET") => {
  const value = String(path || "")
    .split(/[?#]/, 1)[0]
    .toLowerCase();
  if (/session|login|logout|auth/.test(value)) return "authentication";
  if (/parent\/results|parent\/exam-schedule/.test(value))
    return "parent_results";
  if (/attendance/.test(value)) return "parent_attendance";
  if (/homework/.test(value)) return "parent_homework";
  if (/communication|message/.test(value)) return "parent_messages";
  if (/fees?|receipt/.test(value)) return "parent_fees";
  if (/school-week|timetable|course-plan/.test(value))
    return "parent_school_week";
  if (/parent-coming-up|calendar/.test(value)) return "parent_coming_up";
  if (/zero-period/.test(value)) return "parent_daily_test";
  if (/student-360|student\/profile|parent\/profile/.test(value))
    return "parent_profile";
  return ["GET", "HEAD"].includes(String(method || "GET").toUpperCase())
    ? "api_read"
    : "api_write";
};

const allowedValue = (set, value, fallback = "") => {
  const text = String(value || "")
    .trim()
    .toLowerCase();
  return set.has(text) ? text : fallback;
};

const boundedDuration = (value) => {
  const duration = Number(value);
  if (!Number.isFinite(duration) || duration < 0) return undefined;
  return Math.min(120000, Math.round(duration));
};

const currentPathname = () =>
  typeof window === "undefined" ? "" : window.location?.pathname || "";

export const sanitizeClientDiagnostic = (
  event,
  payload = {},
  pathname = currentPathname(),
) => {
  const safeEvent = allowedValue(ALLOWED_EVENTS, event);
  if (!safeEvent) return null;
  const derivedRouteName = resolveSafeRouteName(pathname);
  const routeName = ALLOWED_ROUTE_NAMES.has(payload.routeName)
    ? payload.routeName
    : derivedRouteName;
  const roleCategory = allowedValue(
    ALLOWED_ROLES,
    payload.roleCategory,
    resolveRoleCategory(pathname),
  );
  const operation = allowedValue(
    ALLOWED_OPERATIONS,
    payload.operation,
    routeName.startsWith("parent_") ? routeName : "api_read",
  );
  const durationMs = boundedDuration(payload.durationMs);
  const failureCategory = allowedValue(
    ALLOWED_FAILURES,
    payload.failureCategory,
  );
  const authOutcome = allowedValue(ALLOWED_AUTH_OUTCOMES, payload.authOutcome);
  const record = {
    schemaVersion: DIAGNOSTICS_SCHEMA_VERSION,
    event: safeEvent,
    ts: new Date().toISOString(),
    releaseSha: releaseSha(),
    clientVersion: clientVersion(),
    routeName,
    roleCategory,
    operation,
    online:
      typeof payload.online === "boolean"
        ? payload.online
        : typeof navigator !== "undefined"
          ? navigator.onLine !== false
          : true,
  };
  if (durationMs !== undefined) record.durationMs = durationMs;
  if (failureCategory) record.failureCategory = failureCategory;
  if (authOutcome) record.authOutcome = authOutcome;
  if (typeof payload.superseded === "boolean")
    record.superseded = payload.superseded;
  if (typeof payload.recovered === "boolean")
    record.recovered = payload.recovered;
  return record;
};

const sanitizeStoredRecord = (record) => {
  if (
    record?.schemaVersion !== DIAGNOSTICS_SCHEMA_VERSION ||
    !ALLOWED_EVENTS.has(record?.event)
  )
    return null;
  const safe = sanitizeClientDiagnostic(record.event, record, "");
  if (!safe) return null;
  safe.routeName = ALLOWED_ROUTE_NAMES.has(record.routeName)
    ? record.routeName
    : "public";
  safe.releaseSha = /^[0-9a-f]{40}$/i.test(String(record.releaseSha || ""))
    ? String(record.releaseSha).toLowerCase()
    : "unknown";
  const storedClientVersion = String(record.clientVersion || "");
  safe.clientVersion = /^\d+\.\d+\.\d+(?:-[a-z0-9.-]{1,24})?$/i.test(
    storedClientVersion,
  )
    ? storedClientVersion
    : "unknown";
  if (
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(
      String(record.ts || ""),
    )
  )
    safe.ts = record.ts;
  return safe;
};

const readStoredRecords = () => {
  if (typeof window === "undefined" || !window.localStorage) return [];
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(DIAGNOSTICS_KEY) || "[]",
    );
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(sanitizeStoredRecord)
      .filter(Boolean)
      .slice(0, MAX_RECORDS);
  } catch {
    return [];
  }
};

export const initializeClientDiagnostics = () => {
  if (typeof window === "undefined" || !window.localStorage) return [];
  try {
    const safeRecords = readStoredRecords();
    if (safeRecords.length) {
      window.localStorage.setItem(DIAGNOSTICS_KEY, JSON.stringify(safeRecords));
    } else {
      window.localStorage.removeItem(DIAGNOSTICS_KEY);
    }
    return safeRecords;
  } catch {
    return [];
  }
};

export const recordClientDiagnostic = (event, payload = {}) => {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const record = sanitizeClientDiagnostic(event, payload);
    if (!record) return null;
    const records = readStoredRecords();
    window.localStorage.setItem(
      DIAGNOSTICS_KEY,
      JSON.stringify([record, ...records].slice(0, MAX_RECORDS)),
    );
    return record;
  } catch {
    // Diagnostics must never break the ERP UI.
    return null;
  }
};

export const readClientDiagnostics = () => readStoredRecords();

export const clearClientDiagnostics = () => {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.removeItem(DIAGNOSTICS_KEY);
  } catch {
    // Diagnostics cleanup is best-effort.
  }
};

export { DIAGNOSTICS_KEY, DIAGNOSTICS_SCHEMA_VERSION, MAX_RECORDS };
